"use client";

/**
 * LoginScreen – light split-card login (desktop) / single-column (mobile).
 *
 * Desktop: centered split card (dark welcome panel + light form panel).
 * Mobile: single column with the light form card only.
 * One tree for both: login.css switches layout per media query, so the
 * server HTML already fits the phone (no squeezed split card before hydration).
 *
 * Auth wiring lives in LoginForm (publicAuthClient.signIn.email → redirect "/home").
 * The `email-changed` / `error` params of the email-change confirmation link
 * are rendered by LoginForm and stripped from the URL here.
 */

import { ld } from "@/lib/theme/light-dark";
import { useIsMobile } from "@/app/(dashboard)/_components/task_workspace/inbox/mobile/use-is-mobile";
import { usePathname, useRouter, useSearchParams } from "next/navigation";
import { type ReactElement, useEffect } from "react";
import "./login.css";
import { useNativeAppKind } from "@/lib/native-app/native-app-context";
import { useTranslations } from "next-intl";
import { DotGridBackdrop } from "../../_components/marketing/dot-grid-backdrop";
import { LoginForm } from "./login-form";
import { AppLoginScreen } from "./login-screen-app";
import { DemoCta, DemoFooterLink, WebsiteBackLink } from "./login-screen-parts";
import { INK, PAGE_BG } from "./login-shared";
import {
  BrandLockup,
  LoginWelcomePanel,
  TrustLine,
} from "./login-welcome-panel";
import {
  type PortalLoginBranding,
  PortalLoginScreen,
} from "./portal-login-screen";

const FONT_STACK =
  "var(--font-inter), system-ui, -apple-system, 'Segoe UI', sans-serif";

/** Wide header: brand left, demo CTA right. */
function WideHeader(): ReactElement {
  return (
    <header
      style={{
        position: "relative",
        zIndex: 1,
        width: "100%",
        maxWidth: 1180,
        margin: "0 auto",
        height: 74,
        display: "flex",
        alignItems: "center",
        justifyContent: "space-between",
        padding: "0 32px",
        boxSizing: "border-box",
      }}
    >
      <BrandLockup variant="light" />
      <DemoCta />
    </header>
  );
}

/** Phone header: smaller brand left, „← Zur Website“ right. */
function NarrowHeader(): ReactElement {
  const t = useTranslations("public.loginScreen");
  return (
    <div
      style={{
        position: "relative",
        zIndex: 1,
        height: 58,
        display: "flex",
        alignItems: "center",
        justifyContent: "space-between",
        padding: "0 18px",
      }}
    >
      <BrandLockup variant="light" size={28} />
      <WebsiteBackLink label={t("website")} />
    </div>
  );
}

/** Staff web login; `.mklogin-wide` / `.mklogin-narrow` parts show per width. */
function ResponsiveLogin(): ReactElement {
  const t = useTranslations("public.loginScreen");
  return (
    <div
      className="mklogin mklogin-page"
      style={{ fontFamily: FONT_STACK, background: PAGE_BG, color: INK }}
    >
      <div className="mklogin-wide">
        <DotGridBackdrop tone="light" focus="top" />
        <WideHeader />
      </div>
      <div className="mklogin-narrow">
        <DotGridBackdrop
          tone="light"
          focus="top"
          style={{ inset: "0 0 auto 0", height: 360 }}
        />
        <NarrowHeader />
      </div>

      <main className="mklogin-main">
        <div className="mklogin-card">
          <div className="mklogin-wide">
            <LoginWelcomePanel />
          </div>

          <div className="mklogin-form-panel">
            <div className="mklogin-wide">
              <h2
                style={{
                  fontWeight: 600,
                  fontSize: 26,
                  letterSpacing: "-0.02em",
                  color: INK,
                  margin: 0,
                }}
              >
                {t("melde")}
              </h2>
            </div>
            <div className="mklogin-narrow">
              <h1
                style={{
                  fontFamily: FONT_STACK,
                  fontWeight: 600,
                  fontSize: 30,
                  lineHeight: 1.09,
                  letterSpacing: "-0.035em",
                  margin: 0,
                }}
              >
                {t.rich("schoenDassDuWiederDaBist", {
                  em: (chunks) => (
                    <em style={{ fontStyle: "normal" }}>{chunks}</em>
                  ),
                })}
              </h1>
              <p
                style={{
                  fontSize: 14,
                  lineHeight: 1.55,
                  color: ld("rgba(11,15,25,0.6)", "rgba(250,250,250,0.6)"),
                  margin: "11px 0 0",
                }}
              >
                {t("meldeMachDaWeiter")}
              </p>
            </div>

            <LoginForm density="responsive" />

            <div className="mklogin-demo-footer">
              <DemoFooterLink />
            </div>
            <div className="mklogin-wide">
              <div style={{ marginTop: 20 }}>
                <TrustLine />
              </div>
            </div>
          </div>
        </div>

        <div className="mklogin-narrow">
          <div style={{ margin: "20px 14px 0" }}>
            <TrustLine />
          </div>
        </div>
      </main>
    </div>
  );
}

export function LoginScreen({
  branding,
  portalHost = false,
}: {
  /** Org branding when served on a portal subdomain; null = staff/marketing. */
  readonly branding?: PortalLoginBranding | null;
  /** True on any portalzugang.de host → portal login (never staff marketing). */
  readonly portalHost?: boolean;
}): ReactElement {
  const router = useRouter();
  const pathname = usePathname();
  const searchParams = useSearchParams();
  const isMobile = useIsMobile();
  const nativeApp = useNativeAppKind();

  // Clean the confirmation-link params out of the address bar once LoginForm
  // has read them (it reads on mount, so it always wins this race). `error`
  // goes too — it belongs to that redirect, not to the login page.
  useEffect(() => {
    if (searchParams.get("email-changed") !== "1") return;
    const next = new URLSearchParams(searchParams.toString());
    next.delete("email-changed");
    next.delete("error");
    const qs = next.toString();
    // history-replace: keeps this history entry (redirect or same-screen query)
    router.replace(qs ? `${pathname}?${qs}` : pathname, { scroll: false });
  }, [pathname, router, searchParams]);

  if (branding) {
    return <PortalLoginScreen branding={branding} compact={isMobile} />;
  }
  // Bare apex / unknown subdomain on a portal host: neutral portal login
  // (no staff-only "Demo buchen" marketing CTA for tenants).
  if (portalHost) {
    return (
      <PortalLoginScreen
        branding={{ org_name: null, logo_url: null, brand_color: null }}
        compact={isMobile}
      />
    );
  }
  if (nativeApp === "staff") return <AppLoginScreen />;
  return <ResponsiveLogin />;
}
